import {
  addDays,
  addWeeks,
  differenceInCalendarDays,
  format,
  isSameYear,
  parseISO,
  startOfISOWeek,
} from 'date-fns';

/**
 * `28 Aug`: the day first, the month in three letters. An instant shows the
 * day it is where the coach sits; a calendar date (`YYYY-MM-DD`) shows itself,
 * which only `parseISO` gets right west of UTC.
 */
export function dayAndMonth(value: string) {
  return format(parseISO(value), 'd MMM');
}

/** `17 Sep 2026`: every full date the pages print. */
export function fullDate(instant: string) {
  return format(instant, 'd MMM yyyy');
}

/** `14:20`: the time on the coach's clock. */
export function clockTime(instant: string) {
  return format(instant, 'HH:mm');
}

/** `Today` or `Yesterday` for a day that is one of them where the coach sits. */
export function relativeDay(day: string, now: Date) {
  const days = differenceInCalendarDays(now, parseISO(day));
  return days === 0 ? 'Today' : days === 1 ? 'Yesterday' : null;
}

/** `Thu 17 Sep`; a day of another year carries it, `Wed 17 Sep 2025`. */
export function weekdayDate(day: string, now: Date) {
  const date = parseISO(day);
  return format(date, isSameYear(date, now) ? 'EEE d MMM' : 'EEE d MMM yyyy');
}

/** When a record was made: `Today · 14:20`, `Yesterday · 08:12`, then `Tue 15 Sep · 21:40`. */
export function dayAndTime(instant: string, now: Date) {
  const day = relativeDay(instant, now) ?? weekdayDate(instant, now);
  return `${day} · ${clockTime(instant)}`;
}

/** A date as the calendar where the coach sits shows it, `YYYY-MM-DD`. */
export function localDate(now: Date) {
  return format(now, 'yyyy-MM-dd');
}

/** The zone every bucketed read is cut in: the browser's. */
export function browserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The last `weeks` calendar weeks up to and including the one `today` is in,
 * as the API takes a range: Monday first, the end exclusive.
 */
export function lastWeeks(today: string, weeks: number) {
  const thisMonday = startOfISOWeek(parseISO(today));
  return {
    fromDate: localDate(addWeeks(thisMonday, 1 - weeks)),
    toDate: localDate(addWeeks(thisMonday, 1)),
  };
}

/**
 * The last `days` days up to the end of `today`, as instants: whole local
 * days, so every caller on one day asks for the same range.
 */
export function lastDays(today: string, days: number) {
  const tomorrow = addDays(parseISO(today), 1);
  return {
    fromDateTime: addDays(tomorrow, -days).toISOString(),
    toDateTime: tomorrow.toISOString(),
  };
}

/** Today on the coach's calendar: the one place the page's reads take it from. */
export function today() {
  return localDate(new Date());
}
